import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Layout, usePageTitle } from "@/components/site/Layout";
import { PriceBreakdown } from "@/components/site/PriceBreakdown";
import { api } from "@/lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [b, setB] = useState(null);
  const [state, setState] = useState("checking");
  usePageTitle("Booking Confirmation | Garage Getaway Beaumont");
  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/status/${sid}`);
        setB(data);
        if (data.status === "confirmed") return setState("confirmed");
        if (["expired", "cancelled"].includes(data.status)) return setState("failed");
      } catch {
        return setState("notfound");
      }
      if (++tries >= 12) return setState("pending");
      timer = setTimeout(poll, 2500);
    };
    if (sid) poll(); else setState("notfound");
    return () => clearTimeout(timer);
  }, [sid]);
  return (
    <Layout>
      <section className="px-5 py-16 sm:py-24 max-w-2xl mx-auto" data-testid="booking-success-page">
        {state === "checking" && <p className="flex items-center gap-2 text-muted-foreground" data-testid="payment-verifying"><Loader2 className="animate-spin" /> Verifying your payment with Stripe…</p>}
        {state === "confirmed" && b && (
          <div className="fade-up">
            <CheckCircle2 className="w-10 h-10 text-[hsl(var(--sage))]" />
            <h1 className="mt-4 text-4xl sm:text-5xl font-medium" data-testid="booking-confirmed-title">Thank you, {b.first_name}. Your stay is confirmed.</h1>
            {b.test_mode && <p className="mt-4 text-sm bg-[hsl(35_80%_90%)] px-3 py-2 rounded" data-testid="confirmation-test-note">Test mode: this is a test reservation and no real payment was taken.</p>}
            <p className="mt-4 text-muted-foreground">Reservation <strong data-testid="booking-ref">{b.ref}</strong> · {b.nights} night{b.nights > 1 ? "s" : ""}. A confirmation email is on its way with your arrival details.</p>
            <div className="mt-8 border border-border rounded-lg bg-white p-6"><PriceBreakdown quote={{ ...b.quote, is_sample: false }} /></div>
            <p className="mt-6 text-sm text-muted-foreground">Reminder: the studio is reached by stairs, and free parking is available on the property.</p>
          </div>
        )}
        {state === "pending" && <p data-testid="payment-pending">We're still waiting for Stripe to confirm your payment. Please refresh in a minute. You'll also receive an email once it's confirmed.</p>}
        {(state === "failed" || state === "notfound") && (
          <div data-testid="payment-failed"><AlertCircle className="w-8 h-8 text-destructive" /><h1 className="mt-3 text-3xl">We couldn't confirm this reservation</h1><p className="mt-3 text-muted-foreground">The payment was not completed or the hold expired. No booking was made.</p></div>
        )}
        <Link to="/" className="inline-block mt-10 underline underline-offset-4" data-testid="back-home-link">Back to home</Link>
      </section>
    </Layout>
  );
}
